import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UsePlpSidQueryHandlersParams {
  sidFromUrl?: string;
  sid?: string;
  pincode?: string;
  onOpenPincodePopup: (onSaved?: (nextSid?: string) => void) => void;
  onNoStoreFound?: () => void;
}

function normalizeSid(sid: string | undefined): string | undefined {
  const trimmed = typeof sid === 'string' ? sid.trim() : '';
  return trimmed ? trimmed : undefined;
}

export function usePlpSidQueryHandlers({
  sidFromUrl,
  sid,
  pincode,
  onOpenPincodePopup,
  onNoStoreFound,
}: UsePlpSidQueryHandlersParams) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleActivateStoreDesigns = useCallback(() => {
    if (sidFromUrl) return;

    const sidTrimmed = normalizeSid(sid);
    if (!sidTrimmed) {
      // Pincode already stored but no stores were found → skip popup, show empty state
      if (pincode) {
        onNoStoreFound?.();
        return;
      }
      onOpenPincodePopup((nextSidFromPopup) => {
        const nextSid = normalizeSid(nextSidFromPopup);
        if (!nextSid) {
          onNoStoreFound?.();
          return;
        }

        const params = new URLSearchParams(location.search);
        params.set('sid', nextSid);
        navigate({
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : '',
        });
      });

      return;
    }

    const params = new URLSearchParams(location.search);
    params.set('sid', sidTrimmed);
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    });
  }, [sidFromUrl, sid, pincode, onOpenPincodePopup, onNoStoreFound, location.search, location.pathname, navigate]);

  const handleShowAllDesigns = useCallback(() => {
    if (!sidFromUrl) return;

    const params = new URLSearchParams(location.search);
    params.delete('sid');
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    });
  }, [sidFromUrl, location.search, location.pathname, navigate]);

  return { handleActivateStoreDesigns, handleShowAllDesigns };
}

